import { formatDate } from '@/shared/lib/dateUtils'
import { estiloDeBoton } from '@/shared/lib/estilosDeBoton'
import { Pen, Trash2 } from "lucide-react";
import type { ColumnDef } from '@/features/configuracion/catalogos/config/catalogs.config';
import type { BaseCatalogItem } from '@/features/configuracion/catalogos/types/catalogs.types';
import { EsqueletoTabla } from '@/shared/components/Esqueleto';

interface CatalogTableProps<T extends BaseCatalogItem> {
  rows: T[];
  columns: Array<ColumnDef<T>>;
  loading?: boolean;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  /** A-16: devuelve la clase de salida de la fila que se esta eliminando. */
  claseDeFila?: (id: string) => string;
  onRestore?: (row: T) => void;
  canEdit?: boolean;
  canDelete?: boolean;
}

//

function looksLikeISODate(v: any): v is string {
  return typeof v === "string" && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(v);
}

function formatRelative(iso?: string | null) {
  if (!iso) return "-";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = d.getTime() - now.getTime();
  const abs = Math.abs(diffMs);
  const sec = Math.round(abs / 1000);
  const min = Math.round(sec / 60);
  const hr = Math.round(min / 60);
  const day = Math.round(hr / 24);
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  if (sec < 60) return rtf.format(Math.sign(diffMs) * -sec, "seconds");
  if (min < 60) return rtf.format(Math.sign(diffMs) * -min, "minutes");
  if (hr < 24) return rtf.format(Math.sign(diffMs) * -hr, "hours");
  return rtf.format(Math.sign(diffMs) * -day, "days");
}

function CatalogTable<T extends BaseCatalogItem>({
  rows,
  columns,
  loading,
  onEdit,
  onDelete,
  claseDeFila,
  onRestore,
  canEdit = true,
  canDelete = true,
}: CatalogTableProps<T>) {
  if (loading) {
    // A-01: silueta con el mismo numero de columnas que la tabla real.
    return <EsqueletoTabla filas={5} columnas={columns.length} />;
  }

  if (!rows.length) {
    return (
      <div className="p-6 text-gray-500">No se encontraron registros.</div>
    );
  }

  return (
    <div className="bg-white rounded-lg overflow-hidden border border-[#E5E7EB]">
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-[#E5E7EB]">
        <thead className="bg-[#FAF9FF]">
          <tr>
            {columns.map((c) => (
              <th
                key={String(c.key)}
                className="px-6 py-4 text-left text-sm font-medium text-[#1B2128] tracking-wider"
              >
                {c.header}
              </th>
            ))}
            <th className="px-6 py-4"></th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-[#E1E1E1]">
          {rows.map((row) => (
            <tr key={row.id} className={`${claseDeFila?.(row.id) ?? ""} ${row.deletedAt ? "opacity-60 hover:bg-[#F4F7FB] transition-colors" : "hover:bg-[#F4F7FB] transition-colors"}`}>
              {columns.map((c) => {
                const value: any = (row as any)[String(c.key)];
                return (
                  <td
                    key={String(c.key)}
                    className={`px-4 py-3 text-sm text-gray-900 ${canEdit ? 'cursor-pointer' : ''}`}
                     onClick={() => {
                      if(canEdit) {
                        onEdit?.(row)
                      }
                    }}
                  >
                    {c?.render ? (
                      c.render(value, row)
                    ) : c?.key === "createdAt" ? (
                      <p className="text-gray-500 italic">
                        {formatDate(value)}
                      </p>
                    ) : looksLikeISODate(value) ? (
                      <time title={value} className="text-gray-700">
                        {formatRelative(value)}
                      </time>
                    ) : c?.key === "descripcion" ? (
                      <div className="relative group max-w-[180px]">
                        <p className="truncate text-gray-700">
                          {String(value ?? "")}
                        </p>
                        <div className="pointer-events-none absolute left-0 top-full z-20 mt-1 hidden w-72 rounded-xl border border-gray-200 bg-white p-2 text-xs text-gray-800 shadow-lg group-hover:block">
                          {String(value ?? "")}
                        </div>
                      </div>
                    ) : (
                      String(value ?? "")
                    )}
                  </td>
                );
              })}
              <td className="px-4 py-2 text-right text-sm">
                {row.deletedAt ? (
                  <button
                    onClick={() => onRestore?.(row)}
                    className={estiloDeBoton('exito')}
                  >
                    Restaurar
                  </button>
                ) : (
                  <div className="inline-flex gap-2">
                    {canEdit && (
                      <button
                        onClick={() => onEdit?.(row)}
                        className="text-[#315FB9] hover:text-[#254E8C] mr-1"
                      >
                        <Pen className="h-4 w-4" />
                      </button>
                    )}
                    {canDelete && (
                      <button
                        onClick={() => onDelete?.(row)}
                        className="text-[#EF4444] hover:text-[#EF4444]/80"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </div>
  );
}

export default CatalogTable;
